import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, RefreshCw, Search, Trash2, XCircle } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import {
  Badge,
  Card,
  FieldBase,
  SecondaryButton,
} from "../B2CManagement/b2cUi";
import { partnerApiRequest, resolvePartnerFileUrl } from "../../../util/API";

const normalizeText = (value) => String(value ?? "").trim();

const ownerFromProduct = (product) => {
  const email = normalizeText(product?.ownerEmail);
  if (email) return email;
  const userId = normalizeText(product?.userId);
  if (userId.startsWith("usr_")) return userId.slice(4);
  return userId || "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const statusLabel = (value) => {
  const key = normalizeText(value).toLowerCase();
  if (key === "pending_approval") return "Pending Approval";
  if (key === "approved") return "Approved";
  if (key === "rejected") return "Rejected";
  if (key === "draft") return "Draft";
  return normalizeText(value) || "Unknown";
};

const statusTone = (value) => {
  const key = normalizeText(value).toLowerCase();
  if (key === "approved") return "green";
  if (key === "rejected") return "rose";
  if (key === "pending_approval") return "yellow";
  return "gray";
};

const PartnerProduct = () => {
  const [q, setQ] = useState("");
  const [state, setState] = useState({ loading: false, error: "" });
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 50, total: 0 });
  const [actingId, setActingId] = useState("");
  const [actingType, setActingType] = useState("");

  const visibleItems = useMemo(
    () =>
      (Array.isArray(items) ? items : []).filter(
        (item) => normalizeText(item?.status).toLowerCase() === "pending_approval"
      ),
    [items]
  );

  const fetchList = async (opts = {}) => {
    const nextPage = Number(opts.page || meta.page || 1);
    const limit = Number(opts.limit || meta.limit || 50);

    setState({ loading: true, error: "" });
    try {
      const params = {
        page: nextPage,
        limit,
        status: "pending_approval",
        ...(normalizeText(q) ? { q: normalizeText(q) } : {}),
      };

      const res = await partnerApiRequest({
        method: "get",
        path: "/products/admin",
        params,
      });
      const payload = res?.data || {};
      setItems(Array.isArray(payload?.data) ? payload.data : []);
      setMeta({
        page: Number(payload?.meta?.page || nextPage),
        limit: Number(payload?.meta?.limit || limit),
        total: Number(payload?.meta?.total || 0),
      });
      setState({ loading: false, error: "" });
    } catch (error) {
      setItems([]);
      setMeta({ page: 1, limit: 50, total: 0 });
      setState({
        loading: false,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load partner products",
      });
    }
  };

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doApprove = async (id) => {
    const productId = normalizeText(id);
    if (!productId) return;
    setActingId(productId);
    setActingType("approve");
    setState((prev) => ({ ...prev, error: "" }));

    try {
      await partnerApiRequest({
        method: "post",
        path: `/products/admin/${encodeURIComponent(productId)}/approve`,
      });
      await fetchList({ page: 1 });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to approve product",
      }));
    } finally {
      setActingId("");
      setActingType("");
    }
  };

  const doReject = async (id) => {
    const productId = normalizeText(id);
    if (!productId) return;
    const reason = window.prompt("Reject reason (optional):", "");

    setActingId(productId);
    setActingType("reject");
    setState((prev) => ({ ...prev, error: "" }));

    try {
      await partnerApiRequest({
        method: "post",
        path: `/products/admin/${encodeURIComponent(productId)}/reject`,
        data: { reason: normalizeText(reason) },
      });
      await fetchList({ page: 1 });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to reject product",
      }));
    } finally {
      setActingId("");
      setActingType("");
    }
  };

  const doDelete = async (id) => {
    const productId = normalizeText(id);
    if (!productId) return;
    const ok = window.confirm("Delete this product permanently?");
    if (!ok) return;

    setActingId(productId);
    setActingType("delete");
    setState((prev) => ({ ...prev, error: "" }));

    try {
      await partnerApiRequest({
        method: "delete",
        path: `/products/admin/${encodeURIComponent(productId)}`,
      });
      await fetchList({ page: 1 });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete product",
      }));
    } finally {
      setActingId("");
      setActingType("");
    }
  };

  return (
    <AdminPageShell
      title="Partner Products"
      description="Approve, reject, or remove products submitted by partners."
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="yellow">{visibleItems.length} pending</Badge>
          <Badge tone="gray">{meta.total} total in query</Badge>
        </div>
      }
      actions={
        <SecondaryButton
          type="button"
          onClick={() => fetchList({ page: 1 })}
          disabled={state.loading}
        >
          <RefreshCw className="h-4 w-4" />
          {state.loading ? "Loading..." : "Refresh"}
        </SecondaryButton>
      }
    >
      <div className="space-y-6">
        <Card className="p-5">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
            <div className="max-w-2xl">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  className={`${FieldBase} pl-10`}
                  placeholder="Search by product, owner, category, brand"
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") fetchList({ page: 1 });
                  }}
                />
              </div>
            </div>

            <SecondaryButton type="button" onClick={() => fetchList({ page: 1 })} disabled={state.loading}>
              Search
            </SecondaryButton>
          </div>

          {state.error ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">
              {state.error}
            </div>
          ) : null}
        </Card>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Image
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Product
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Owner
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Submitted
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                      Loading...
                    </td>
                  </tr>
                ) : visibleItems.length ? (
                  visibleItems.map((item) => {
                    const id = normalizeText(item?._id || item?.id);
                    const busy = actingId === id;
                    const firstPhotoUrl = item?.photos?.[0]?.fileUrl || item?.photos?.[0];
                    const imageUrl = resolvePartnerFileUrl(firstPhotoUrl);

                    return (
                      <tr key={id} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50/70">
                        <td className="px-4 py-4">
                          <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
                            {imageUrl ? (
                              <img alt="Product" src={imageUrl} className="h-10 w-10 object-cover" />
                            ) : (
                              <span className="text-xs text-slate-400">N/A</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-semibold text-slate-900">{item.name || "Untitled product"}</div>
                          <div className="text-xs text-slate-500 font-mono">{id || "-"}</div>
                        </td>
                        <td className="px-4 py-4 text-slate-700">{ownerFromProduct(item)}</td>
                        <td className="px-4 py-4">
                          <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}</Badge>
                        </td>
                        <td className="px-4 py-4 text-slate-700">{formatDate(item.createdAt || item.submittedAt)}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <SecondaryButton
                              type="button"
                              disabled={busy || state.loading}
                              onClick={() => doApprove(id)}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              {busy && actingType === "approve" ? "Approving..." : "Approve"}
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              disabled={busy || state.loading}
                              onClick={() => doReject(id)}
                            >
                              <XCircle className="h-4 w-4" />
                              {busy && actingType === "reject" ? "Rejecting..." : "Reject"}
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              disabled={busy || state.loading}
                              onClick={() => doDelete(id)}
                            >
                              <Trash2 className="h-4 w-4" />
                              {busy && actingType === "delete" ? "Deleting..." : "Delete"}
                            </SecondaryButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                      No products awaiting review.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-white">
            <div className="text-xs font-semibold text-slate-600">
              Page {meta.page} | Showing {visibleItems.length} | Total {meta.total}
            </div>
            <div className="flex gap-2">
              <SecondaryButton
                type="button"
                onClick={() => fetchList({ page: Math.max(1, meta.page - 1) })}
                disabled={state.loading || meta.page <= 1}
              >
                Prev
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() => fetchList({ page: meta.page + 1 })}
                disabled={state.loading || items.length < meta.limit}
              >
                Next
              </SecondaryButton>
            </div>
          </div>
        </Card>
      </div>
    </AdminPageShell>
  );
};

export default PartnerProduct;
